import { useDownloadRFSXlsx, useRFSReassignBulk, useRFSs } from "@/api/rfs";
import { FieldInputDate } from "@/components/form/field-input-date";
import { TableCell } from "@/components/table/table-cell";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalError,
  swalErrorDefault,
  swalLoading,
  swalSuccess,
} from "@/utils/lib/swal";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./RFSTable.module.css";

import { useAuth } from "../auth/useAuth";
import { REQUIRED_MESSAGE, ROLE, STATUS } from "@/utils/constant";
import { useSearchParams } from "react-router-dom";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { DefaultButton, SubmitButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FormContainer } from "@/components/form/form-container";
import { UserData, useUsers } from "@/api/user";
import { FieldCombobox } from "@/components/form/field-combobox";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios, { AxiosError } from "axios";
import { TableHeaderFilter } from "@/components/table/table-header-filter";
import { useMasterParams } from "@/api/master-param";
import { generateParams } from "@/utils/helper/generateParams";

const reassignFormSchema = z.object({
  picLinknet: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type ReassignFormSchema = z.infer<typeof reassignFormSchema>;

export function RFSTable() {
  const auth = useAuth();
  const { roles } = auth.getUser();
  const notAllowedCreateRFS = hasCommonElement(roles, [
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
    ROLE.MONITORING_TEAM,
    ROLE.VENDOR_VIEW,
  ]);
  const isAdministrator = hasCommonElement(roles, [ROLE.ADMINISTRATOR]);
  const isVendorView = roles.includes(ROLE.VENDOR_VIEW);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = Object.fromEntries(searchParams.entries());

  const [selectedRow, setSelectedRow] = useState<string[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig(filters);

  const { data: rfss, isLoading: isLoadingRFSData } = useRFSs(dataConfig);
  const { mutate: downloadRFSXlsx, isPending: isDownloading } =
    useDownloadRFSXlsx();

  const [startDate, setStartDate] = useState<Date | null | undefined>(null);
  const [endDate, setEndDate] = useState<Date | null | undefined>(null);
  const filterForm = useForm({
    defaultValues: filters,
  });

  const [reassignOpen, setReassignOpen] = useState(false);
  const reassignForm = useForm<ReassignFormSchema>({
    resolver: zodResolver(reassignFormSchema),
  });
  const reassignFormId = useId();

  const { mutate: reassignBulk } = useRFSReassignBulk();
  const customSpec = useCustomRFSSpec();

  const [userSearch, setUserSearch] = useState("");
  const { data: validators } = useUsers({
    role: ROLE.VALIDATOR_DOCUMENT_OFFICER.toUpperCase(),
  });
  const { data: administrators } = useUsers({
    role: ROLE.ADMINISTRATOR.toUpperCase(),
  });

  const users = [
    ...(validators?.content ?? []),
    ...(administrators?.content ?? []),
  ];

  function resetDateFilter() {
    setStartDate(null);
    setEndDate(null);
    setSearchParams();
  }

  function handleDownload(formFilter: object) {
    const filterTable: DataConfig = {
      ...dataConfig,
      page: undefined,
      limit: undefined,
    };

    const params = {
      ...formFilter,
      ...filters,
      ...filterForm.getValues(),
      ...filterTable,
    };

    downloadRFSXlsx(
      {
        queryParams: generateParams(params),
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleReassignDialogChange() {
    reassignForm.reset();
    setReassignOpen((prev) => !prev);
  }

  function handleReassign(values: ReassignFormSchema) {
    setReassignOpen(false);
    swalLoading.fire();
    reassignBulk(
      {
        data: selectedRow.map((id) => ({
          id,
          picLinknet: {
            id: values.picLinknet,
          },
        })),
      },
      {
        onSuccess: () => {
          setSelectedRow([]);
          swalSuccess.fire({
            text: "RFS has been reassigned",
          });
        },
        onError: (error) => {
          const message = axios.isAxiosError(error)
            ? (error as AxiosError<{ data: string }>).response?.data?.data
            : error.message;
          swalError.fire({
            text: message,
          });
        },
      },
    );
  }

  return (
    <>
      <div className="z-10 max-w-fit">
        <Dialog open={reassignOpen} onOpenChange={handleReassignDialogChange}>
          <DialogTrigger asChild>
            <DefaultButton
              mood="axiata-blue-outline"
              disabled={!selectedRow.length || isVendorView}
            >
              Reassign
            </DefaultButton>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reassign</DialogTitle>
            </DialogHeader>
            <FormContainer
              form={reassignForm}
              id={reassignFormId}
              onSubmit={handleReassign}
            >
              <FieldCombobox
                name="picLinknet"
                label="Linknet PIC"
                search={userSearch}
                setSearch={setUserSearch}
                options={(users ?? [])?.map((user: UserData) => ({
                  value: user.id,
                  label: user.username,
                }))}
                hasRequiredAsterisk
              />
            </FormContainer>
            <DialogFooter>
              <SubmitButton form={reassignFormId} />
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="-mt-[76px]">
        <TableForm
          form={filterForm}
          setDataConfig={setDataConfig}
          createPath={notAllowedCreateRFS ? null : "/rfs/rfs/create"}
          createLabel="Create RFS"
          handleDownload={handleDownload}
          handleResetFilter={resetDateFilter}
          isDownloading={isDownloading}
        >
          <div className="flex gap-4">
            <FieldInputDate
              name="startCreatedDate"
              label="Start Date"
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
              onChange={(date) => setStartDate(date)}
            />
            <FieldInputDate
              name="endCreatedDate"
              label="End Date"
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              onChange={(date) => setEndDate(date)}
            />
          </div>
        </TableForm>
      </div>
      <TableDefault
        className={styles.rfsTable}
        hideActionColumn
        spec={customSpec}
        data={rfss}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        //@ts-expect-error tipe selectedRow tidak akurat di TableLowCode
        selectedRow={selectedRow}
        //@ts-expect-error tipe setSelectedRow tidak akurat di TableLowCode
        setSelectedRow={setSelectedRow}
        customCell={TableCell}
        loading={isLoadingRFSData}
        customBulkCell={({ row }) => {
          return (
            <CheckBox
              row={row}
              isAdministrator={isAdministrator}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
            />
          );
        }}
        searchFilterByColumns={true}
        customFilterByColumns={TableHeaderFilter}
      />
    </>
  );
}

function CheckBox({
  row,
  isAdministrator,
  selectedRow,
  setSelectedRow,
}: {
  row: {
    [key: string]: unknown;
  };
  isAdministrator: boolean;
  selectedRow: string[];
  setSelectedRow: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const statusRfs = row.statusRfsSubmission;
  const isRevise = statusRfs === STATUS.RFS.REVISION_DATA;
  const isCanceled = statusRfs === STATUS.RFS.CANCELED;

  return (
    <input
      type="checkbox"
      className={`form-checkbox rounded border bg-neutral-50 text-axiata-blue focus:border-none focus:outline-axiata-blue disabled:border-none disabled:bg-neutral-300`}
      checked={selectedRow?.includes((row as { id: string }).id)}
      disabled={!isAdministrator || isCanceled || isRevise}
      onChange={() => {
        if (selectedRow && !selectedRow.includes((row as { id: string }).id)) {
          setSelectedRow((prev) => [...prev, (row as { id: string }).id]);
        } else {
          setSelectedRow((prev) =>
            prev.filter((id) => id !== (row as { id: string }).id),
          );
        }
      }}
    />
  );
}

function useCustomRFSSpec() {
  const { data: filterStatus } = useMasterParams({
    paramType: "STATUS_INTEGRATION_XL",
    active: "true",
    asc: "true",
  });

  const filterOptions = filterStatus?.content.map((status) => ({
    key: status.value,
    label: status.value,
  }));

  const customSpec = generateCustomSpec({
    path: "",
    canBulk: true,
    canEdit: false,
    fields: [
      {
        name: "custom_action",
        label: "Action",
        sortable: false,
        searchable: false,
      },
      {
        name: "clusterId",
        label: "Cluster ID",
        searchable: true,
      },
      {
        name: "status",
        label: "Status",
        searchable: true,
        select_options: {
          options: [
            {
              key: "PREPARATION_DATA",
              label: "Prepare & Validate",
            },
            {
              key: "REJECTED_DOCUMENT_RFS",
              label: "Rejected",
            },
            {
              key: "REVIEW_BY_LN",
              label: "In Review",
            },
            {
              key: "CANCELED",
              label: "Cancelled",
            },
            {
              key: "REVISION_DATA",
              label: "Draft (Invalid)",
            },
            ...(filterOptions ?? []),
          ],
        },
      },
      {
        name: "drawingIdAbd",
        label: "Drawing ID ABD",
        searchable: true,
      },
      {
        name: "projectId",
        label: "Project ID",
        searchable: true,
      },
      {
        name: "cityTown",
        label: "City Town",
        searchable: true,
      },
      {
        name: "fdtId",
        label: "FDTID",
        searchable: true,
      },
      {
        name: "projectName",
        label: "Project Name",
        searchable: true,
      },
      {
        name: "oltId",
        label: "OLT-ID",
        searchable: true,
      },
      {
        name: "odnType",
        label: "ODN Type",
        searchable: true,
      },
      {
        name: "combineStatus",
        label: "Combine Status",
        searchable: true,
      },
      {
        name: "totalHpRfs",
        label: "Total HP RFS (ABD)",
        searchable: true,
      },
      {
        name: "trackerdataApd.tenantName",
        label: "Tenant Name",
        searchable: true,
      },
      {
        name: "trackerdataApd.vendor",
        label: "Vendor",
        searchable: true,
      },
      {
        name: "picVendor",
        label: "Vendor PIC",
        searchable: true,
      },
      {
        name: "picLinknet",
        label: "PIC Linknet",
        searchable: true,
      },
      {
        name: "updatedDate",
        label: "Last Update",
        searchable: true,
      },
      {
        name: "createdDate",
        label: "Created Date",
        searchable: true,
      },
    ],
  });

  return customSpec;
}
